import { CareTopic } from '@app/shared/care-topic';

import { RoutingStep, RoutingStepVariables } from '../__generated__/routing-step-graphql.service.types';
import { AppointmentType } from '../appointment-type';
import { StructuredReasonForVisit } from '../structured-reason-for-visit';
import { AppointmentRoutingStepName } from './appointment-routing-steps';

export const CARE_OPTION_NAME_BOOK_APPOINTMENT = 'BookAppointment';

export class AppointmentRoutingState {
  appointmentTypeId: number;
  appointmentTypeLocked = false;
  careOptionId: string;
  careOptionLocked = true;
  careOptionName = CARE_OPTION_NAME_BOOK_APPOINTMENT;
  final: boolean;
  reasonForVisit: string;
  rfvRequired: boolean;
  routingSessionId: string;
  serviceAreaId: number;
  stepName: AppointmentRoutingStepName;
  surveySystemName: string;
  topicName: CareTopic;
  appointmentTypes: AppointmentType[] = [];

  // structured reason for visit
  structuredReasonForVisit: StructuredReasonForVisit;

  /**
   * Populate the Appointment Routing State from a GraphQL response
   *
   * @param data
   */
  fromGraphQL(data: RoutingStep): void {
    this.final = data.routingStep.final;
    this.routingSessionId = data.routingStep.routingSessionId;
    this.stepName = data.routingStep.stepName as AppointmentRoutingStepName;
    this.topicName = data.routingStep.topicName as CareTopic;

    const { stepContext } = data.routingStep;
    if (stepContext) {
      this.rfvRequired = stepContext.rfvRequired;
      if (stepContext.survey) {
        this.surveySystemName = stepContext.survey.systemName;
      }
      if (stepContext.appointmentType) {
        this.appointmentTypeId = parseInt(stepContext.appointmentType.id, 10);
      } else {
        this.appointmentTypeId = null;
      }

      if (stepContext.appointmentTypes && stepContext.appointmentTypes.length > 0) {
        this.appointmentTypes = stepContext.appointmentTypes.map(apptType => AppointmentType.fromGraphQL(apptType));
      }
    }
  }

  /**
   * Get a collection of GraphQL variables based on the current Appointment Routing State
   *
   * @returns
   */
  toGraphQL(): RoutingStepVariables {
    const variables: RoutingStepVariables = {
      appointmentTypeLocked: this.appointmentTypeLocked,
      careOptionId: this.careOptionId,
      careOptionLocked: this.careOptionLocked,
      careOptionName: this.careOptionName,
      reasonForVisit: this.reasonForVisit,
      routingSessionId: this.routingSessionId,
      stepName: this.stepName,
      topicName: this.topicName,
      surveySystemName: this.surveySystemName,
    };
    if (this.appointmentTypeId) {
      variables.appointmentTypeId = this.appointmentTypeId.toString();
    }
    if (this.serviceAreaId) {
      variables.serviceAreaId = this.serviceAreaId.toString();
    }

    if (this.structuredReasonForVisit && this.structuredReasonForVisit.selectedRFV) {
      variables.clinicalConceptId = this.structuredReasonForVisit.selectedRFV.id;
      variables.appointmentTypeIds = this.structuredReasonForVisit.selectedRFV.appointmentTypeIds.map(id =>
        id.toString(),
      );
      variables.structuredVisitReasonFallback = this.structuredReasonForVisit.selectedRFV.structuredVisitReasonFallback;
      variables.structuredVisitReasonQuickTapSelected = this.structuredReasonForVisit.selectedRFV.structuredVisitReasonQuickTapSelected;
    }

    return variables;
  }

  setStructuredReasonForVisit(metadata: StructuredReasonForVisit) {
    this.structuredReasonForVisit = metadata;
  }

  setSelectedReasonForVisit(selectedRFV: StructuredReasonForVisit['selectedRFV']) {
    this.structuredReasonForVisit = {
      ...this.structuredReasonForVisit,
      selectedRFV,
    };
  }
}
